/**
 * @fileoverview The action buttons of a My Page target.
 * @description Shows one button for each action the backend says the caller may take on the target
 * right now (`allowedActions`): update stage, reopen, reassign, request a reassignment, send
 * payment details and confirm payment. It never decides who may do what; it only shows what the
 * backend allowed, and the backend rechecks every action when it is taken.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import Button from '@/shared/components/atoms/Button'

import type { MyPageTargetAction } from '@/features/sales/types/my-page.types'

interface MyPageActionBarProps {
  actions: MyPageTargetAction[]
  onAction: (action: MyPageTargetAction) => void
}

/**
 * @description The QA locator of an action's button.
 * @param {MyPageTargetAction} action - The action.
 * @returns {string} For example `my-page-action-update-stage`.
 */
function testIdOf(action: MyPageTargetAction): string {
  return `my-page-action-${action.toLowerCase().replaceAll('_', '-')}`
}

/**
 * @component
 * @param {MyPageActionBarProps} props - The allowed actions and the handler.
 * @param {MyPageTargetAction[]} props.actions - What the caller may do, from the backend.
 * @param {(action: MyPageTargetAction) => void} props.onAction - Called with the chosen action.
 */
function MyPageActionBar({ actions, onAction }: MyPageActionBarProps) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-wrap gap-2.5" data-testid="my-page-action-bar">
      {actions.map((action) => (
        <div key={action} className="w-fit">
          <Button
            type="button"
            size="md"
            variant={action === 'CONFIRM_PAYMENT' ? 'primary' : 'secondary'}
            onClick={() => onAction(action)}
            data-testid={testIdOf(action)}
          >
            {t(`sales.myWork.actions.${action}`)}
          </Button>
        </div>
      ))}
    </div>
  )
}

export default MyPageActionBar
